import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Product from './Products/Product';
import ProductVariant from './Products/ProductVariant';
import LoadMore from './Products/LoadMore';
import { setScrollState } from '../../actions';
import api from '../../api';
import enrichProductsWithBoldOptions from '../../utils/enrichBoldOptions';

const OPTIONAL_SECTIONS = ['hatsAndHair', 'display', 'accessories', 'keychain'];

const variantMatchesSkintone = (variant, skintone) => {
    if (!variant || !skintone) return false;

    const target = skintone.toLowerCase();
    const metafieldValue = variant.skintoneValue?.value?.toLowerCase();
    if (metafieldValue === target) return true;

    const title = variant.title?.toLowerCase() || '';
    const titleTokens = {
        yellow: ['yellow'],
        light: ['light'],
        medium: ['medium'],
        nougat: ['nougat'],
        dark: ['dark'],
    };

    return (titleTokens[target] || [target]).some((token) => title.includes(token));
};

const createNoSelectionProduct = (section) => {
    const labels = {
        hatsAndHair: 'No Hat or Hair',
        display: 'No Display',
        accessories: 'No Accessories',
        keychain: 'No Keychain',
    };

    return {
        node: {
            id: `no-selection:${section}`,
            title: labels[section] || 'No Selection',
            availableForSale: true,
            variants: {
                nodes: [
                    {
                        id: `no-selection:${section}:variant`,
                        availableForSale: true,
                    },
                ],
            },
        },
        cursor: `no-selection-${section}`,
        boldProductOptions: ['0', {}],
        _isNoSelection: true,
    };
};

/**
 * Product browser component
 *
 * Shows the products available within a section
 *
 * @author John Noel <john.noel@joipolloi.com>
 * @author Matt Haynes <matthew.haynes@four.agency>
 * @package Minifigs
 */
const Products = ({
    products,
    section,
    selectedProducts = {},
    onSelectNamedProduct = () => {},
    selected = null,
    onSelect = () => {},
    onRemove = () => {},
    onCustomise = () => {},
    projectTitle = '',
    skintone = '',
    currentBuildIndex = 0,
    dispatch = () => {},
    isSearchResults = false,
    // Search pagination props (optional)
    searchHasNextPage = false,
    searchAfterCursor = null,
    onLoadMoreSearch = null,
    searchLoading = false,
    // External ref to control scroll position (optional)
    externalScrollRef = null,
    relatedProductsRaw = [],
    selectedCategoryHandle = undefined,
    prefetchProductDetails = null,
}) => {
    const scrollContainer = useRef(null);
    const [enrichedRelatedProducts, setEnrichedRelatedProducts] = useState(relatedProductsRaw);

    useEffect(() => {
        if (externalScrollRef) {
            externalScrollRef.current = scrollContainer.current;
        }
    }, [externalScrollRef]);

    useEffect(() => {
        let lastFiredExpanded = false;
        const handleScroll = () => {
            if (!scrollContainer.current) return;
            const scrollTop = scrollContainer.current.scrollTop;
            const isScrolled = scrollTop >= 25;
            dispatch(setScrollState(isScrolled));
            if (isScrolled) {
                if (!lastFiredExpanded) {
                    window.dispatchEvent(new CustomEvent('builder:scrollExpandTrigger'));
                    lastFiredExpanded = true;
                }
            } else {
                lastFiredExpanded = false;
            }
        };

        const container = scrollContainer.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);

            handleScroll();

            return () => {
                container.removeEventListener('scroll', handleScroll);
            };
        }
    }, [dispatch]);

    const isInCollection = (product, targetHandle) => {
        const node = product?.node || product;
        const edges = node?.collections?.edges || [];
        const target = String(targetHandle).toLowerCase();

        return edges.some((edge) => (edge?.node?.handle || '').toLowerCase() === target);
    };

    // Convenience: heads / legs collections
    const isHeadProduct = (product) => {
        return isInCollection(product, 'all-heads-new-1');
    };

    const isLegProduct = (product) => {
        return isInCollection(product, 'all-legs');
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!Array.isArray(relatedProductsRaw) || relatedProductsRaw.length === 0) {
                setEnrichedRelatedProducts(relatedProductsRaw);
                return;
            }
            try {
                const enriched = await enrichProductsWithBoldOptions(relatedProductsRaw, api);
                if (!cancelled) setEnrichedRelatedProducts(enriched);
            } catch (e) {
                if (!cancelled) setEnrichedRelatedProducts(relatedProductsRaw);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [relatedProductsRaw]);

    const accessorySelectedIds =
        section === 'accessories' && Array.isArray(selectedProducts?.accessories)
            ? selectedProducts.accessories.map((acc) => acc?.product?.id).filter(Boolean)
            : [];

    // Related products: show all matching related products at the top
    let derivedProducts = products;
    if (Array.isArray(enrichedRelatedProducts) && enrichedRelatedProducts.length && selectedCategoryHandle) {
        const lowerHandle = selectedCategoryHandle.toLowerCase();
        const matching = enrichedRelatedProducts.filter((rp) => {
            const edges = rp?.collections?.edges || [];
            return edges.some((e) => (e?.node?.handle || '').toLowerCase() === lowerHandle);
        });

        if (matching.length) {
            const seenRelatedIds = new Set();
            const relatedWrapped = [];
            matching.forEach((m) => {
                if (!m?.id) return;
                if (seenRelatedIds.has(m.id)) return;
                seenRelatedIds.add(m.id);
                relatedWrapped.push({
                    node: m,
                    cursor: `related-${m.id}`,
                    // Preserve any existing boldProductOptions from enrichment; fall back to placeholder
                    boldProductOptions: m.boldProductOptions || ['0', {}],
                    _isRelated: true,
                });
            });

            if (relatedWrapped.length) {
                // Filter out related products from original list
                const filteredOriginals = products.filter((p) => {
                    const pid = p?.node?.id ? p.node.id : p?.id;
                    return !seenRelatedIds.has(pid);
                });
                derivedProducts = [...relatedWrapped, ...filteredOriginals];
            }
        }
    }

    // Inject any selected product(s) that are NOT in the current listing so they always appear at the top.
    const injectedSelected = [];
    const haveId = (p) => (p?.node?.id ? p.node.id : p?.id);
    const hasProductId = (pid) =>
        derivedProducts.some((p) => {
            const existingId = haveId(p);
            return existingId === pid;
        });

    if (section === 'accessories') {
        if (Array.isArray(selectedProducts?.accessories)) {
            selectedProducts.accessories.forEach((acc, index) => {
                const prod = acc?.product;
                if (prod?.id && !hasProductId(prod.id)) {
                    injectedSelected.push({
                        node: prod,
                        cursor: `selected-${prod.id}-${index}`,
                        boldProductOptions: acc?.boldProductOptions || ['0', {}],
                        _isInjectedSelected: true,
                    });
                }
            });
        }
    } else {
        // Primary selected product for non-accessory sections
        if (selected?.product?.id && !hasProductId(selected.product.id)) {
            injectedSelected.push({
                node: selected.product,
                cursor: `selected-${selected.product.id}`,
                boldProductOptions: selected?.boldProductOptions || ['0', {}],
                _isInjectedSelected: true,
            });
        }
        // Cross-section handling: ensure keychain appears in display section if selected elsewhere
        if (section === 'display') {
            const keychainProd = selectedProducts?.keychain?.product;
            if (keychainProd?.id && !hasProductId(keychainProd.id)) {
                injectedSelected.push({
                    node: keychainProd,
                    cursor: `selected-${keychainProd.id}-keychain`,
                    boldProductOptions: selectedProducts?.keychain?.boldProductOptions || ['0', {}],
                    _isInjectedSelected: true,
                });
            }
        }
    }

    if (injectedSelected.length) {
        derivedProducts = [...injectedSelected, ...derivedProducts];
    }

    // Reorder so that any currently selected product(s) appear first for easier discovery.
    if (section === 'accessories' && accessorySelectedIds.length > 0) {
        const selectedOrder = accessorySelectedIds;
        const selectedSet = new Set(selectedOrder);
        const selectedItems = [];
        const remainingItems = [];
        derivedProducts.forEach((p) => {
            const pid = haveId(p);
            if (selectedSet.has(pid)) {
                selectedItems.push(p);
            } else {
                remainingItems.push(p);
            }
        });
        selectedItems.sort((a, b) => selectedOrder.indexOf(haveId(a)) - selectedOrder.indexOf(haveId(b)));
        if (selectedItems.length) {
            derivedProducts = [...selectedItems, ...remainingItems];
        }
    } else if (section !== 'accessories' && selected?.product?.id) {
        const targetId = selected.product.id;
        const idx = derivedProducts.findIndex((p) => haveId(p) === targetId);
        if (idx > 0) {
            const chosen = derivedProducts[idx];
            derivedProducts = [chosen, ...derivedProducts.slice(0, idx), ...derivedProducts.slice(idx + 1)];
        }
    }

    // Prepend "No selection" tile for optional sections
    if (OPTIONAL_SECTIONS.includes(section) && !isSearchResults) {
        const noSelectionProduct = createNoSelectionProduct(section);
        derivedProducts = [noSelectionProduct, ...derivedProducts];
    }

    return (
        <div className={`products-container large`}>
            {section === 'bodies' && !isSearchResults && (
                <p className="bodies-info">
                    <img
                        src="https://cdn.shopify.com/s/files/1/0223/5446/0736/files/icon-palette_2cd45be0-5921-4428-8d88-25a37f5e2335.svg?v=1759396488"
                        alt="Customise"
                        width="20"
                        height="20"
                    />{' '}
                    Most bodies are available in multiple colours
                </p>
            )}
            <ul ref={scrollContainer} className="products products-3 le-products">
                {derivedProducts.map((product) => {
                    // Normalise variants: prefer nodes; if only edges provided, map to nodes
                    const variantNodesRaw = product?.node?.variants?.nodes;
                    const variantNodesFromEdges = product?.node?.variants?.edges
                        ? product.node.variants.edges.map((e) => e.node)
                        : [];
                    const variantNodes =
                        Array.isArray(variantNodesRaw) && variantNodesRaw.length > 0
                            ? variantNodesRaw
                            : variantNodesFromEdges;

                    const isHead = section === 'heads' || isHeadProduct(product);
                    const isLeg = section === 'legs' || isLegProduct(product);
                    // Heads: always render variants
                    // Legs: render variants when product actually has multiple variants (>=2)
                    const hasMultipleVariants = variantNodes.length > 1;
                    const shouldRenderVariants = !isSearchResults && (isHead || (isLeg && hasMultipleVariants));

                    if (shouldRenderVariants) {
                        const matchingVariants = variantNodes.filter((variant) =>
                            variantMatchesSkintone(variant, skintone)
                        );

                        // If no matching variants (e.g. legs product without skintone variants), fallback to product card
                        if (!matchingVariants || matchingVariants.length === 0) {
                            const isSelectedAccessory =
                                section === 'accessories' && accessorySelectedIds.includes(product.node.id);
                            const singleSectionSelected =
                                section !== 'accessories' &&
                                selected !== null &&
                                product.node.id == selected.product.id;
                            return (
                                <div
                                    className={`product-wrapper product-regular-wrapper ${
                                        product._isRelated ? 'related-product' : ''
                                    }`}
                                    key={product.node.id}
                                >
                                    <Product
                                        product={product.node}
                                        section={section}
                                        selectedProducts={selectedProducts}
                                        isSelected={isSelectedAccessory || singleSectionSelected}
                                        onSelect={onSelect}
                                        onSelectNamedProduct={onSelectNamedProduct}
                                        projectTitle={projectTitle}
                                        onRemove={onRemove}
                                        onCustomise={onCustomise}
                                        boldProductOptions={product.boldProductOptions}
                                        currentBuildIndex={currentBuildIndex}
                                        skintone={skintone}
                                        prefetchProductDetails={prefetchProductDetails}
                                        isSearchResults={isSearchResults}
                                    />
                                </div>
                            );
                        }

                        return (
                            <div
                                className={`product-wrapper product-variant-wrapper ${
                                    product._isRelated ? 'related-product' : ''
                                }`}
                                key={product.node.id}
                            >
                                {matchingVariants.map((variant) => (
                                    <ProductVariant
                                        key={variant.id}
                                        parentProduct={product.node}
                                        selectedProducts={selectedProducts}
                                        product={variant}
                                        section={section}
                                        productTitle={product.node.title}
                                        isSelected={selected !== null && variant.id == selected.productMatchAttr?.id}
                                        onSelect={onSelect}
                                        projectTitle={projectTitle}
                                        boldProductOptions={product.boldProductOptions}
                                        onRemove={onRemove}
                                        onCustomise={onCustomise}
                                    />
                                ))}
                            </div>
                        );
                    }

                    const isSelectedAccessory =
                        section === 'accessories' && accessorySelectedIds.includes(product.node.id);
                    // Treat keychain variant as slected in the display tab, if selected...
                    let singleSectionSelected =
                        section !== 'accessories' && selected !== null && product.node.id == selected.product.id;
                    if (!singleSectionSelected && section === 'display') {
                        try {
                            const keychainVariantId = selectedProducts?.keychain?.product?.variants?.nodes?.[0]?.id;
                            const currentVariantId = product.node?.variants?.nodes?.[0]?.id;
                            if (keychainVariantId && currentVariantId) {
                                const keychainNumeric = keychainVariantId.split('/')?.pop();
                                const currentNumeric = currentVariantId.split('/')?.pop();
                                if (keychainNumeric && currentNumeric && keychainNumeric === currentNumeric) {
                                    singleSectionSelected = true;
                                }
                            }
                        } catch (e) {
                            console.warn('Keychain cross-section select check failed', e);
                        }
                    }

                    // Calculate isSelected for no-selection tiles
                    const isNoSelectionTile = product._isNoSelection === true;
                    let calculatedIsSelected = isSelectedAccessory || singleSectionSelected;
                    if (isNoSelectionTile) {
                        if (section === 'accessories') {
                            calculatedIsSelected =
                                !Array.isArray(selectedProducts?.accessories) ||
                                selectedProducts.accessories.length === 0;
                        } else {
                            calculatedIsSelected = selected === null || selected === undefined;
                        }
                    }

                    return (
                        <div
                            className={`product-wrapper product-regular-wrapper-2 ${
                                product._isRelated ? 'related-product' : ''
                            }`}
                            key={product.node.id}
                        >
                            <Product
                                product={product.node}
                                section={section}
                                selectedProducts={selectedProducts}
                                isSelected={calculatedIsSelected}
                                onSelect={onSelect}
                                onSelectNamedProduct={onSelectNamedProduct}
                                projectTitle={projectTitle}
                                onRemove={onRemove}
                                onCustomise={onCustomise}
                                boldProductOptions={product.boldProductOptions}
                                currentBuildIndex={currentBuildIndex}
                                skintone={skintone}
                                prefetchProductDetails={prefetchProductDetails}
                                isSearchResults={isSearchResults}
                                isNoSelectionTile={isNoSelectionTile}
                            />
                        </div>
                    );
                })}

                <div className="load-more-slide">
                    <LoadMore
                        swiperState={null}
                        scrollContainer={scrollContainer}
                        isSearchMode={isSearchResults}
                        onLoadMoreSearch={onLoadMoreSearch}
                        searchHasNextPage={searchHasNextPage}
                        searchAfterCursor={searchAfterCursor}
                        searchLoading={searchLoading}
                    />
                </div>
            </ul>
        </div>
    );
};

Products.propTypes = {
    products: PropTypes.array.isRequired,
    section: PropTypes.string.isRequired,
    selectedProducts: PropTypes.object,
    // When section==='accessories', selected will be ignored and accessories array used instead
    selected: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    onSelect: PropTypes.func,
    onSelectNamedProduct: PropTypes.func,
    onRemove: PropTypes.func,
    onCustomise: PropTypes.func,
    projectTitle: PropTypes.string,
    skintone: PropTypes.string,
    currentBuildIndex: PropTypes.number,
    isSearchResults: PropTypes.bool,
    dispatch: PropTypes.func,
    searchHasNextPage: PropTypes.bool,
    searchAfterCursor: PropTypes.string,
    onLoadMoreSearch: PropTypes.func,
    searchLoading: PropTypes.bool,
    externalScrollRef: PropTypes.shape({ current: PropTypes.any }),
    relatedProductsRaw: PropTypes.array,
    selectedCategoryHandle: PropTypes.string,
    prefetchProductDetails: PropTypes.func,
};

const mapStateToProps = (state) => {
    const currentIndex = state.currentBuildIndex || 0;
    const build = state.builds?.[currentIndex] || {};
    return {
        relatedProductsRaw: build.relatedProducts || [],
        selectedCategoryHandle: state.browser?.selectedCategory?.handle,
        currentBuildIndex: currentIndex,
    };
};

export default connect(mapStateToProps)(Products);
